/**
 * The typed HTTP client. Every byte the dashboard reads or writes goes through
 * `request()` — no component ever calls `fetch`.
 *
 * Shape mirrors `docs/CRM-BUILD-CONTRACT.md` §3 one-for-one, so a route on the
 * server and a call site in the UI are always one hop apart.
 */

import type {
  Activity,
  ActivityType,
  AnalyticsOverview,
  AnalyticsResponseTimes,
  ApiError as ApiErrorPayload,
  Attachment,
  AuditEntry,
  BreakdownRow,
  BugNature,
  BugPatch,
  BugReport,
  BugReportDetail,
  BugReportMine,
  BugReportType,
  BugSeverity,
  BugStatus,
  BugSummary,
  BulkRequest,
  BulkResponse,
  Company,
  Contact,
  FilterSet,
  FunnelStep,
  ID,
  Inquiry,
  InquiryType,
  LeaderboardRow,
  ListQuery,
  LoginRequest,
  Note,
  Paginated,
  PipelineStage,
  Priority,
  RangeKey,
  Region,
  Role,
  SavedView,
  SessionResponse,
  SortSpec,
  StageKey,
  Status,
  Tag,
  TagColor,
  Task,
  TaskBatchDeleteResponse,
  TaskBatchResponse,
  TimeseriesResponse,
  User,
  Webhook,
  WebhookInput,
  WebhookPatch,
} from '@shared/types';

/* ────────────────────────────────────────────────────────────────────────────
   Configuration
   ──────────────────────────────────────────────────────────────────────────── */

export const API_BASE = '/api/v1';

/**
 * Fired on `window` when the server rejects a request with 401. The router
 * listens and bounces to `/login`, preserving the current location.
 */
export const UNAUTHORIZED_EVENT = 'af:unauthorized';

export interface UnauthorizedDetail {
  path: string;
  requestId: string;
}

/** Requests hang forever otherwise; an error state beats a permanent skeleton. */
const DEFAULT_TIMEOUT_MS = 30_000;

/* ────────────────────────────────────────────────────────────────────────────
   Errors
   ──────────────────────────────────────────────────────────────────────────── */

export type ApiErrorCode = ApiErrorPayload['error']['code'];

/** Why the request failed: the server said so, the wire broke, or the body lied. */
export type ApiErrorKind = 'http' | 'network' | 'parse' | 'timeout';

/**
 * The only error type the query layer has to understand. Carries the server's
 * `code`, `message`, per-field validation problems and the `requestId` that ties
 * a red toast to a line in the server log.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly fields: Record<string, string> | undefined;
  readonly requestId: string;
  readonly kind: ApiErrorKind;
  readonly path: string;

  constructor(init: {
    code: ApiErrorCode;
    message: string;
    status: number;
    requestId: string;
    path: string;
    kind?: ApiErrorKind;
    fields?: Record<string, string>;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.requestId = init.requestId;
    this.path = init.path;
    this.kind = init.kind ?? 'http';
    this.fields = init.fields;
  }

  /** 4xx means "you asked wrong" — retrying is pointless. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  get isRetryable(): boolean {
    return this.kind !== 'http' || this.status >= 500 || this.status === 429;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Field-level problems exist → render them inline instead of as a toast. */
  get isValidation(): boolean {
    return this.code === 'unprocessable' || this.code === 'bad_request';
  }

  static is(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }
}

function isAbort(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'AbortError' || error.name === 'TimeoutError')
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Query-string serialisation
   ──────────────────────────────────────────────────────────────────────────── */

export type QueryPrimitive = string | number | boolean | null | undefined;
export type QueryInput = Record<string, QueryPrimitive | QueryPrimitive[] | object>;

/**
 * `undefined`/`null`/`''` are dropped so the URL stays short and cache keys stay
 * stable; arrays repeat the key; objects are JSON-encoded (the `filters` /
 * `sort` contract).
 */
export function toSearchParams(input: QueryInput | undefined): URLSearchParams {
  const params = new URLSearchParams();
  if (!input) return params;

  for (const key of Object.keys(input).sort()) {
    const value = input[key];
    if (value === undefined || value === null || value === '') continue;

    if (Array.isArray(value)) {
      // An array of objects is one structured parameter (`sort`), not a repeated
      // scalar: repeating it would send `sort=[object Object]` and the server
      // would reject the page with "`sort` must be valid JSON".
      if (value.some((item) => typeof item === 'object' && item !== null)) {
        params.set(key, JSON.stringify(value));
        continue;
      }
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue;
        params.append(key, String(item));
      }
    } else if (typeof value === 'object') {
      params.set(key, JSON.stringify(value));
    } else {
      params.set(key, String(value));
    }
  }
  return params;
}

/** Groupings `/tasks/group-counts` can total. Due windows are plain filters. */
export type TaskGroupBy = 'assignee' | 'priority';

/** One band's authoritative size. `key` is `''` for the unassigned bucket. */
export interface TaskGroupCount {
  key: string;
  total: number;
}

/** Serialises the `ListQuery` contract exactly as the server parses it. */
export function listQueryParams(query: ListQuery | undefined): QueryInput {
  if (!query) return {};
  const hasFilters = Boolean(query.filters && query.filters.conditions.length > 0);
  const hasSort = Boolean(query.sort && query.sort.length > 0);
  return {
    q: query.q?.trim() || undefined,
    filters: hasFilters ? query.filters : undefined,
    sort: hasSort ? query.sort : undefined,
    cursor: query.cursor || undefined,
    limit: query.limit,
    viewId: query.viewId || undefined,
  };
}

function buildUrl(path: string, query?: QueryInput): string {
  const params = toSearchParams(query);
  const qs = params.toString();
  return qs ? `${API_BASE}${path}?${qs}` : `${API_BASE}${path}`;
}

/* ────────────────────────────────────────────────────────────────────────────
   Request core
   ──────────────────────────────────────────────────────────────────────────── */

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  query?: QueryInput;
  /** JSON-encoded unless it is already `FormData`. */
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Suppress the global `af:unauthorized` broadcast (login + session probe). */
  silentUnauthorized?: boolean;
  timeoutMs?: number;
}

function withTimeout(signal: AbortSignal | undefined, ms: number): AbortSignal | undefined {
  if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') {
    return signal;
  }
  const timeout = AbortSignal.timeout(ms);
  if (!signal) return timeout;
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([signal, timeout]);
  return signal;
}

function emitUnauthorized(detail: UnauthorizedDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<UnauthorizedDetail>(UNAUTHORIZED_EVENT, { detail }));
}

async function readError(
  response: Response,
  path: string,
  silentUnauthorized: boolean,
): Promise<ApiError> {
  const headerRequestId = response.headers.get('x-request-id') ?? '';
  let code: ApiErrorCode = statusToCode(response.status);
  let message = defaultMessage(response.status);
  let fields: Record<string, string> | undefined;
  let requestId = headerRequestId;

  try {
    const payload = (await response.json()) as Partial<ApiErrorPayload>;
    if (payload && typeof payload === 'object' && payload.error) {
      code = payload.error.code ?? code;
      message = payload.error.message || message;
      fields = payload.error.fields;
      requestId = payload.error.requestId || requestId;
    }
  } catch {
    /* Non-JSON error body (proxy HTML, empty 502) — the defaults above stand. */
  }

  if (response.status === 401 && !silentUnauthorized) {
    emitUnauthorized({ path, requestId });
  }

  return new ApiError({ code, message, fields, requestId, status: response.status, path });
}

function statusToCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 422:
      return 'unprocessable';
    case 429:
      return 'rate_limited';
    default:
      return 'internal';
  }
}

function defaultMessage(status: number): string {
  switch (status) {
    case 401:
      return 'Your session has expired. Sign in again to continue.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return 'That record no longer exists.';
    case 409:
      return 'Someone else changed this record first.';
    case 429:
      return 'Too many requests — slow down for a moment.';
    default:
      return status >= 500 ? 'The server had a problem completing that request.' : 'Request failed.';
  }
}

/** The one place a `fetch` happens. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    query,
    body,
    signal,
    headers,
    silentUnauthorized = false,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  } = options;

  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    signal: withTimeout(signal, timeoutMs),
  };
  if (body !== undefined) {
    init.body = isForm ? (body as FormData) : JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), init);
  } catch (error) {
    if (isAbort(error)) {
      if (signal?.aborted) throw error; // caller cancelled — let Query treat it as such
      throw new ApiError({
        code: 'internal',
        kind: 'timeout',
        message: 'The request timed out. Check your connection and try again.',
        status: 0,
        requestId: '',
        path,
      });
    }
    throw new ApiError({
      code: 'internal',
      kind: 'network',
      message: 'Could not reach the server.',
      status: 0,
      requestId: '',
      path,
    });
  }

  if (!response.ok) throw await readError(response, path, silentUnauthorized);

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError({
      code: 'internal',
      kind: 'parse',
      message: 'The server sent a response the app could not read.',
      status: response.status,
      requestId: response.headers.get('x-request-id') ?? '',
      path,
    });
  }
}

/** Binary variant — CSV export and attachment downloads. */
export async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const { query, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options;
  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method: options.method ?? 'GET',
      credentials: 'include',
      signal: withTimeout(signal, timeoutMs),
    });
  } catch (error) {
    if (isAbort(error) && signal?.aborted) throw error;
    throw new ApiError({
      code: 'internal',
      kind: 'network',
      message: 'Could not reach the server.',
      status: 0,
      requestId: '',
      path,
    });
  }
  if (!response.ok) throw await readError(response, path, false);
  return response.blob();
}

/* ────────────────────────────────────────────────────────────────────────────
   Response envelopes that are transport shapes, not domain entities
   ──────────────────────────────────────────────────────────────────────────── */

/** One column of `/inquiries/board`, capped server-side. */
export interface BoardColumn {
  stage: PipelineStage;
  items: Inquiry[];
  /** Total matching the filters, not the capped `items.length`. */
  total: number;
  /**
   * Past the response target with no first reply, matching the same filters —
   * server-side, over the whole column, never over the cards on screen.
   */
  overdue: number;
  nextCursor: string | null;
}

export interface BoardResponse {
  columns: BoardColumn[];
  /** Total across every column, for the header count. */
  total: number;
}

export interface BoardQuery extends Omit<ListQuery, 'cursor'> {
  /** Rows per column. */
  perColumn?: number;
}

/** `⌘K` payload — grouped so the palette can render labelled sections. */
export interface SearchResponse {
  query: string;
  inquiries: Inquiry[];
  contacts: Contact[];
  companies: Company[];
  users: User[];
  tags: Tag[];
  total: number;
}

export interface AnalyticsQuery {
  range?: RangeKey;
  /** Required when `range` is `'custom'`. */
  from?: string;
  to?: string;
  filters?: FilterSet;
}

export interface TimeseriesQuery extends AnalyticsQuery {
  granularity?: TimeseriesResponse['granularity'];
  /** Dimension to split the series by: `stageKey` | `source` | `type` | `region` | `ownerId`. */
  groupBy?: string;
  metric?: 'count' | 'won' | 'score' | 'responseMinutes';
}

export interface BreakdownQuery extends AnalyticsQuery {
  dimension: string;
  limit?: number;
}

export interface LeaderboardQuery extends AnalyticsQuery {
  limit?: number;
}

export interface AuditQuery {
  entity?: string;
  entityId?: ID;
  actorId?: ID;
  action?: AuditEntry['action'];
  cursor?: string;
  limit?: number;
}

/* ── Write payloads ──────────────────────────────────────────────────────── */

export interface InquiryPatch {
  stageKey?: StageKey;
  status?: Status;
  priority?: Priority;
  ownerId?: ID | null;
  region?: Region | null;
  type?: InquiryType;
  subject?: string;
  contactId?: ID | null;
  companyId?: ID | null;
  slaDueAt?: string | null;
  /** Estimated contract value in whole USD; `null` clears it back to unvalued. */
  value?: number | null;
  /** Replaces the whole tag set. */
  tagIds?: ID[];
  fields?: Record<string, string | number | boolean | null>;
}

export interface NoteInput {
  body: string;
  isPinned?: boolean;
}

export interface TaskInput {
  title: string;
  assigneeId?: ID | null;
  dueAt?: string | null;
  priority?: Priority;
}

export interface TaskPatch {
  title?: string;
  assigneeId?: ID | null;
  dueAt?: string | null;
  priority?: Priority;
  /** ISO timestamp to complete, `null` to reopen. */
  completedAt?: string | null;
}

export interface ActivityInput {
  type: ActivityType;
  body: string;
  meta?: Record<string, unknown>;
}

/* ── Messages (the reply thread) ──────────────────────────────────────────────
   The wire shape of `backend/src/routes/messages.ts`. It is declared here rather
   than in `@shared/types` only because that file is owned elsewhere; if it ever
   gains a `Message`, delete this block and import it instead — the fields are
   already name-for-name identical.                                            */

export type MessageChannel = 'email' | 'call' | 'sms' | 'meeting';
export type MessageDirection = 'inbound' | 'outbound';

/**
 * `queued` is the terminal state for anything this product sends today: the row
 * is recorded and durable, and no mail transport exists to advance it. The UI
 * must say "queued" and never "sent" or "delivered" for a message the composer
 * created — see the transport note at the top of the backend route.
 */
export type MessageStatus =
  | 'draft'
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'received'
  | 'logged';

export interface Message {
  id: ID;
  inquiryId: ID;
  contactId: ID | null;
  /** The AlphaForm-side participant: who sent it, or who logged it. */
  userId: ID | null;
  author?: User | null;
  channel: MessageChannel;
  direction: MessageDirection;
  status: MessageStatus;
  subject: string | null;
  body: string;
  fromAddress: string | null;
  toAddress: string | null;
  ccAddress: string | null;
  durationSeconds: number | null;
  externalId: string | null;
  occurredAt: string;
  createdAt: string;
  /** Wall clock at which edit/retract stops being allowed. */
  editableUntil: string | null;
}

export interface MessageInput {
  body: string;
  subject?: string;
  /** Defaults to the inquiry's contact email server-side. */
  to?: string;
  cc?: string;
  direction?: MessageDirection;
  /** `sms` is deliberately not accepted — there is no activity type for it. */
  channel?: 'email' | 'call' | 'meeting';
  occurredAt?: string;
  durationSeconds?: number;
}

export interface MessagePatch {
  body?: string;
  subject?: string | null;
}

/**
 * What `POST /inquiries/:id/messages` returns. `inquiry` and
 * `firstResponseMinutes` are present only when the send moved the record —
 * i.e. it stopped the SLA clock or repainted the score — so a caller can
 * repaint the header without a refetch.
 */
export interface MessageCreated {
  message: Message;
  activity: Activity;
  inquiry?: Inquiry;
  firstResponseMinutes?: number;
}

export interface TagInput {
  name: string;
  color: TagColor;
}

export interface SavedViewInput {
  name: string;
  entity: SavedView['entity'];
  filters: FilterSet;
  sort: SortSpec[];
  columns: string[];
  layout: SavedView['layout'];
  isShared?: boolean;
  isDefault?: boolean;
  icon?: string | null;
  position?: number;
}

export interface UserPatch {
  name?: string;
  title?: string | null;
  role?: Role;
  isActive?: boolean;
}

export interface StagePatch {
  id: ID;
  label?: string;
  position?: number;
  color?: TagColor;
  slaHours?: number | null;
}

/** The stage vocabulary is closed, so a create names the key it is restoring. */
export interface StageCreate {
  key: StageKey;
  label: string;
  color: TagColor;
  slaHours?: number | null;
}

/* ────────────────────────────────────────────────────────────────────────────
   The namespaced surface
   ──────────────────────────────────────────────────────────────────────────── */

const auth = {
  login: (body: LoginRequest, signal?: AbortSignal) =>
    request<SessionResponse>('/auth/login', {
      method: 'POST',
      body,
      signal,
      silentUnauthorized: true,
    }),
  logout: (signal?: AbortSignal) =>
    request<void>('/auth/logout', { method: 'POST', signal, silentUnauthorized: true }),
  /** Session probe: a 401 here is "not signed in", not "session expired". */
  me: (signal?: AbortSignal) =>
    request<SessionResponse>('/auth/me', { signal, silentUnauthorized: true }),
};

const inquiries = {
  list: (query?: ListQuery, signal?: AbortSignal) =>
    request<Paginated<Inquiry>>('/inquiries', { query: listQueryParams(query), signal }),

  board: (query?: BoardQuery, signal?: AbortSignal) =>
    request<BoardResponse>('/inquiries/board', {
      query: { ...listQueryParams(query), perColumn: query?.perColumn },
      signal,
    }),

  get: (id: ID, signal?: AbortSignal) => request<Inquiry>(`/inquiries/${id}`, { signal }),

  patch: (id: ID, patch: InquiryPatch, signal?: AbortSignal) =>
    request<Inquiry>(`/inquiries/${id}`, { method: 'PATCH', body: patch, signal }),

  bulk: (body: BulkRequest, signal?: AbortSignal) =>
    request<BulkResponse>('/inquiries/bulk', { method: 'POST', body, signal }),

  activities: (id: ID, query?: { cursor?: string; limit?: number }, signal?: AbortSignal) =>
    request<Paginated<Activity>>(`/inquiries/${id}/activities`, { query, signal }),

  createActivity: (id: ID, body: ActivityInput, signal?: AbortSignal) =>
    request<Activity>(`/inquiries/${id}/activities`, { method: 'POST', body, signal }),

  notes: (id: ID, signal?: AbortSignal) =>
    request<Paginated<Note>>(`/inquiries/${id}/notes`, { signal }),

  createNote: (id: ID, body: NoteInput, signal?: AbortSignal) =>
    request<Note>(`/inquiries/${id}/notes`, { method: 'POST', body, signal }),

  tasks: (id: ID, signal?: AbortSignal) =>
    request<Paginated<Task>>(`/inquiries/${id}/tasks`, { signal }),

  createTask: (id: ID, body: TaskInput, signal?: AbortSignal) =>
    request<Task>(`/inquiries/${id}/tasks`, { method: 'POST', body, signal }),

  attachments: (id: ID, signal?: AbortSignal) =>
    request<Attachment[]>(`/inquiries/${id}/attachments`, { signal }),

  /**
   * Attach files to an existing record. Multipart, so `request` skips the JSON
   * content type and lets the browser write the boundary. The default timeout
   * is sized for JSON; a 25 MB Revit model over a site connection is not, hence
   * the explicit two minutes.
   */
  uploadAttachments: (id: ID, files: File[], signal?: AbortSignal) => {
    const form = new FormData();
    for (const file of files) form.append('files', file, file.name);
    return request<Attachment[]>(`/inquiries/${id}/attachments`, {
      method: 'POST',
      body: form,
      timeoutMs: 120_000,
      signal,
    });
  },
};

/**
 * The reply thread. `list` is oldest-first — a conversation is read downwards,
 * unlike the activity feed — and paginates forwards from `nextCursor`.
 */
const messages = {
  list: (
    inquiryId: ID,
    query?: { cursor?: string; limit?: number },
    signal?: AbortSignal,
  ) => request<Paginated<Message>>(`/inquiries/${inquiryId}/messages`, { query, signal }),

  create: (inquiryId: ID, body: MessageInput, signal?: AbortSignal) =>
    request<MessageCreated>(`/inquiries/${inquiryId}/messages`, {
      method: 'POST',
      body,
      signal,
    }),

  update: (id: ID, body: MessagePatch, signal?: AbortSignal) =>
    request<Message>(`/messages/${id}`, { method: 'PATCH', body, signal }),

  remove: (id: ID, signal?: AbortSignal) =>
    request<void>(`/messages/${id}`, { method: 'DELETE', signal }),
};

const notes = {
  patch: (id: ID, body: Partial<NoteInput>, signal?: AbortSignal) =>
    request<Note>(`/notes/${id}`, { method: 'PATCH', body, signal }),
  remove: (id: ID, signal?: AbortSignal) =>
    request<void>(`/notes/${id}`, { method: 'DELETE', signal }),
};

const tasks = {
  list: (query?: ListQuery, signal?: AbortSignal) =>
    request<Paginated<Task>>('/tasks', { query: listQueryParams(query), signal }),
  /**
   * `{key,total}[]` for one grouping of the *same* queue `list` would return —
   * the authoritative figure behind a band header when the band is not
   * expressible as a filter (owner, priority).
   */
  groupCounts: (groupBy: TaskGroupBy, query?: ListQuery, signal?: AbortSignal) =>
    request<TaskGroupCount[]>('/tasks/group-counts', {
      query: { ...listQueryParams(query), groupBy },
      signal,
    }),
  patch: (id: ID, body: TaskPatch, signal?: AbortSignal) =>
    request<Task>(`/tasks/${id}`, { method: 'PATCH', body, signal }),
  remove: (id: ID, signal?: AbortSignal) =>
    request<void>(`/tasks/${id}`, { method: 'DELETE', signal }),
  /**
   * The checkbox's whole job. `completedAt` is not a writable field — the
   * server owns the timestamp and rejects a PATCH carrying it ("Unrecognized
   * key(s) in object: 'completedAt'"), which is why this is its own endpoint
   * pair rather than a convenience over `patch`.
   */
  setComplete: (id: ID, complete: boolean, signal?: AbortSignal) =>
    request<Task>(`/tasks/${id}/${complete ? 'complete' : 'reopen'}`, {
      method: 'POST',
      signal,
    }),

  /**
   * The batch half of the three writes above — one request for a whole
   * selection.
   *
   * A bulk gesture is one intent, so it is one call: one optimistic patch, one
   * rollback snapshot, one toast, one retry. Fanning a selection out into a
   * request per row put fifty PATCHes on the wire, tripped this API's own
   * limiter, and left the screen painting changes that were never committed.
   * Each returns the rows as they now stand plus the ids it could not reach —
   * a batch is partial-tolerant, and the caller states the real split.
   */
  patchMany: (ids: readonly ID[], patch: TaskPatch, signal?: AbortSignal) =>
    request<TaskBatchResponse>('/tasks', { method: 'PATCH', body: { ids, patch }, signal }),

  setCompleteMany: (ids: readonly ID[], complete: boolean, signal?: AbortSignal) =>
    request<TaskBatchResponse>('/tasks/complete', {
      method: 'POST',
      body: { ids, completed: complete },
      signal,
    }),

  removeMany: (ids: readonly ID[], signal?: AbortSignal) =>
    request<TaskBatchDeleteResponse>('/tasks', { method: 'DELETE', body: { ids }, signal }),
};

const contacts = {
  list: (query?: ListQuery, signal?: AbortSignal) =>
    request<Paginated<Contact>>('/contacts', { query: listQueryParams(query), signal }),
  get: (id: ID, signal?: AbortSignal) => request<Contact>(`/contacts/${id}`, { signal }),
  inquiries: (id: ID, query?: ListQuery, signal?: AbortSignal) =>
    request<Paginated<Inquiry>>(`/contacts/${id}/inquiries`, {
      query: listQueryParams(query),
      signal,
    }),
};

export interface CompanyInput {
  name: string;
  domain?: string | null;
  website?: string | null;
  region?: Region | null;
  segment?: string | null;
  size?: string | null;
  city?: string | null;
  country?: string | null;
  /** Acknowledge the duplicate-domain conflict and create anyway. */
  allowDuplicate?: boolean;
}

/** One distinct value of an open-vocabulary column, with how many rows carry it. */
export interface Facet {
  value: string;
  count: number;
}

export interface CompanyFacets {
  /** Distinct `segment` values present in the book, most common first. */
  segments: Facet[];
}

const companies = {
  list: (query?: ListQuery, signal?: AbortSignal) =>
    request<Paginated<Company>>('/companies', { query: listQueryParams(query), signal }),
  /**
   * The open vocabularies behind the account list's filters. `segment` is free
   * text on the wire, so the only honest option list is the one the server can
   * see — a hard-coded constant drifts and then offers values nothing matches.
   */
  facets: (signal?: AbortSignal) => request<CompanyFacets>('/companies/facets', { signal }),
  get: (id: ID, signal?: AbortSignal) => request<Company>(`/companies/${id}`, { signal }),
  create: (body: CompanyInput, signal?: AbortSignal) =>
    request<Company>('/companies', { method: 'POST', body, signal }),
  /** Honours the live filter set, exactly like the inquiries export. */
  exportCsvUrl: (query?: ListQuery) => buildUrl('/export/companies.csv', listQueryParams(query)),
  inquiries: (id: ID, query?: ListQuery, signal?: AbortSignal) =>
    request<Paginated<Inquiry>>(`/companies/${id}/inquiries`, {
      query: listQueryParams(query),
      signal,
    }),
  contacts: (id: ID, query?: ListQuery, signal?: AbortSignal) =>
    request<Paginated<Contact>>(`/companies/${id}/contacts`, {
      query: listQueryParams(query),
      signal,
    }),
};

const tags = {
  list: (signal?: AbortSignal) => request<Tag[]>('/tags', { signal }),
  create: (body: TagInput, signal?: AbortSignal) =>
    request<Tag>('/tags', { method: 'POST', body, signal }),
  patch: (id: ID, body: Partial<TagInput>, signal?: AbortSignal) =>
    request<Tag>(`/tags/${id}`, { method: 'PATCH', body, signal }),
  remove: (id: ID, signal?: AbortSignal) =>
    request<void>(`/tags/${id}`, { method: 'DELETE', signal }),
};

/**
 * Outbound webhooks. Workspace configuration, held by the API — the endpoint
 * list used to live in `localStorage`, which made it invisible on the next
 * machine and impossible to deliver from.
 */
const webhooks = {
  list: (signal?: AbortSignal) =>
    request<{ items: Webhook[] }>('/webhooks', { signal }).then((response) => response.items),
  create: (input: WebhookInput, signal?: AbortSignal) =>
    request<Webhook>('/webhooks', { method: 'POST', body: input, signal }),
  patch: (id: ID, patch: WebhookPatch, signal?: AbortSignal) =>
    request<Webhook>(`/webhooks/${id}`, { method: 'PATCH', body: patch, signal }),
  remove: (id: ID, signal?: AbortSignal) =>
    request<{ id: ID }>(`/webhooks/${id}`, { method: 'DELETE', signal }),
  /** One synthetic `webhook.test` event, sent now, even to a paused endpoint. */
  test: (id: ID, signal?: AbortSignal) =>
    request<{ queued: boolean }>(`/webhooks/${id}/test`, { method: 'POST', signal }),
  /** Re-send one past delivery's exact body. */
  redeliver: (deliveryId: ID, signal?: AbortSignal) =>
    request<{ queued: boolean }>(`/webhook-deliveries/${deliveryId}/retry`, {
      method: 'POST',
      signal,
    }),
};

const views = {
  list: (signal?: AbortSignal) => request<SavedView[]>('/saved-views', { signal }),
  /** Every visible view's total, keyed by view id — one request for the whole rail. */
  counts: (signal?: AbortSignal) => request<Record<ID, number>>('/saved-views/counts', { signal }),
  create: (body: SavedViewInput, signal?: AbortSignal) =>
    request<SavedView>('/saved-views', { method: 'POST', body, signal }),
  patch: (id: ID, body: Partial<SavedViewInput>, signal?: AbortSignal) =>
    request<SavedView>(`/saved-views/${id}`, { method: 'PATCH', body, signal }),
  remove: (id: ID, signal?: AbortSignal) =>
    request<void>(`/saved-views/${id}`, { method: 'DELETE', signal }),
};

const pipeline = {
  stages: (signal?: AbortSignal) => request<PipelineStage[]>('/pipeline/stages', { signal }),
  updateStages: (body: StagePatch[], signal?: AbortSignal) =>
    request<PipelineStage[]>('/pipeline/stages', { method: 'PATCH', body, signal }),
  /** Both writes return the whole pipeline in its new order. */
  createStage: (body: StageCreate, signal?: AbortSignal) =>
    request<PipelineStage[]>('/pipeline/stages', { method: 'POST', body, signal }),
  deleteStage: (id: ID, signal?: AbortSignal) =>
    request<void>(`/pipeline/stages/${id}`, { method: 'DELETE', signal }),
};

const users = {
  list: (signal?: AbortSignal) => request<User[]>('/users', { signal }),
  patch: (id: ID, body: UserPatch, signal?: AbortSignal) =>
    request<User>(`/users/${id}`, { method: 'PATCH', body, signal }),
};

const analytics = {
  overview: (query?: AnalyticsQuery, signal?: AbortSignal) =>
    request<AnalyticsOverview>('/analytics/overview', { query: analyticsParams(query), signal }),

  timeseries: (query?: TimeseriesQuery, signal?: AbortSignal) =>
    request<TimeseriesResponse>('/analytics/timeseries', {
      query: {
        ...analyticsParams(query),
        granularity: query?.granularity,
        groupBy: query?.groupBy,
        metric: query?.metric,
      },
      signal,
    }),

  breakdown: (query: BreakdownQuery, signal?: AbortSignal) =>
    request<BreakdownRow[]>('/analytics/breakdown', {
      query: { ...analyticsParams(query), dimension: query.dimension, limit: query.limit },
      signal,
    }),

  funnel: (query?: AnalyticsQuery, signal?: AbortSignal) =>
    request<FunnelStep[]>('/analytics/funnel', { query: analyticsParams(query), signal }),

  responseTimes: (query?: AnalyticsQuery, signal?: AbortSignal) =>
    request<AnalyticsResponseTimes>('/analytics/response-times', {
      query: analyticsParams(query),
      signal,
    }),

  leaderboard: (query?: LeaderboardQuery, signal?: AbortSignal) =>
    request<LeaderboardRow[]>('/analytics/leaderboard', {
      query: { ...analyticsParams(query), limit: query?.limit },
      signal,
    }),
};

function analyticsParams(query: AnalyticsQuery | undefined): QueryInput {
  if (!query) return {};
  return {
    range: query.range,
    from: query.from,
    to: query.to,
    filters: query.filters && query.filters.conditions.length > 0 ? query.filters : undefined,
  };
}

const audit = {
  list: (query?: AuditQuery, signal?: AbortSignal) =>
    request<Paginated<AuditEntry>>('/audit-log', { query: { ...query }, signal }),
};

const files = {
  /** Direct href for `<a download>` / `<img src>` — cookie auth rides along. */
  url: (id: ID) => `${API_BASE}/files/${id}`,
  blob: (id: ID, signal?: AbortSignal) => requestBlob(`/files/${id}`, { signal }),
};

/* ────────────────────────────────────────────────────────────────────────────
   Bug reports

   snake_case, and unix-second timestamps, all the way down — see the fenced
   section in `@shared/types` for why, and `backend/src/routes/bugs.ts` for the
   routes. Filing a report is NOT here: the vendored widget posts `/bugs/submit`
   itself, ahead of the bundle, which is what lets it capture a boot error.
   ──────────────────────────────────────────────────────────────────────────── */

export interface BugQuery {
  status?: BugStatus;
  severity?: BugSeverity;
  page?: string;
  nature?: BugNature;
  report_type?: BugReportType;
  q?: string;
}

const bugs = {
  /** The reporter's own reports. No `context_json` — that is a triage surface. */
  mine: (signal?: AbortSignal) => request<{ bugs: BugReportMine[] }>('/bugs/mine', { signal }),

  /** Open and unconfirmed-on-live. Answers 0 for non-admins rather than 403. */
  unverifiedCount: (signal?: AbortSignal) =>
    request<{ count: number }>('/bugs/unverified-count', { signal }),

  /** Grouped by page, most-open-first. The triage screen's spine. */
  summary: (signal?: AbortSignal) => request<BugSummary>('/admin/bugs/summary', { signal }),

  list: (query?: BugQuery, signal?: AbortSignal) =>
    request<{ bugs: BugReport[] }>('/admin/bugs', { query: { ...query }, signal }),

  get: (id: number, signal?: AbortSignal) =>
    request<BugReportDetail>(`/admin/bugs/${id}`, { signal }),

  patch: (id: number, body: BugPatch, signal?: AbortSignal) =>
    request<{ ok: true; id: number }>(`/admin/bugs/${id}`, { method: 'PUT', body, signal }),

  /**
   * "Confirmed fixed on live" — deliberately not the same flag as
   * `status: 'resolved'`, which only means somebody shipped a change.
   */
  verify: (id: number, verified: boolean, signal?: AbortSignal) =>
    request<{ ok: true; id: number; verified: boolean }>(`/bugs/${id}/verify`, {
      method: 'PUT',
      body: { verified },
      signal,
    }),

  remove: (id: number, signal?: AbortSignal) =>
    request<{ ok: true }>(`/admin/bugs/${id}`, { method: 'DELETE', signal }),

  /** Direct href for `<img src>` — the session cookie authenticates it. */
  screenshotUrl: (id: number, name: string) =>
    `${API_BASE}/bugs/${id}/screenshots/${encodeURIComponent(name)}`,
};

/* ────────────────────────────────────────────────────────────────────────────
   Public surface (marketing site → CRM). Unauthenticated, rate-limited.
   ──────────────────────────────────────────────────────────────────────────── */

const publicApi = {
  inquiry: (body: FormData | Record<string, unknown>, signal?: AbortSignal) =>
    request<{ id: ID; ref: string }>('/public/inquiries', {
      method: 'POST',
      body,
      signal,
      silentUnauthorized: true,
    }),
  assistantSession: (body: Record<string, unknown>, signal?: AbortSignal) =>
    request<{ id: ID }>('/public/assistant-sessions', {
      method: 'POST',
      body,
      signal,
      silentUnauthorized: true,
    }),
};

/* ────────────────────────────────────────────────────────────────────────────
   Export
   ──────────────────────────────────────────────────────────────────────────── */

export const api = {
  auth,
  inquiries,
  messages,
  notes,
  tasks,
  contacts,
  companies,
  tags,
  views,
  webhooks,
  pipeline,
  users,
  analytics,
  audit,
  files,
  bugs,
  public: publicApi,

  /** Multi-entity quick search behind `⌘K`. */
  search: (q: string, signal?: AbortSignal) =>
    request<SearchResponse>('/search', { query: { q }, signal }),

  /** Honours the same filter contract as `/inquiries`. */
  exportCsv: (query?: ListQuery, signal?: AbortSignal) =>
    requestBlob('/export/inquiries.csv', { query: listQueryParams(query), signal }),

  /** Href variant, for a plain anchor when a Blob round-trip is not wanted. */
  exportCsvUrl: (query?: ListQuery) => buildUrl('/export/inquiries.csv', listQueryParams(query)),

  /** SSE endpoint — consumed by `@/lib/realtime`. */
  eventsUrl: (lastEventId?: string) =>
    buildUrl('/events', lastEventId ? { lastEventId } : undefined),
} as const;

export type Api = typeof api;
